import React from 'react';
import { dossierAPI } from '../../services/api';
import { RichText } from '../common/RichText';

export type Niveau = 'laag' | 'midden' | 'hoog' | 'zeer_hoog';
export type Houding = 'zeer_negatief' | 'negatief' | 'neutraal' | 'positief' | 'zeer_positief';
export type StakeholderType = 'individu' | 'organisatie' | 'groep';

export interface Stakeholder {
  id: string;
  naam: string;
  type: StakeholderType;
  groep: string;
  invloed: Niveau;
  belang: Niveau;
  houding: Houding;
  belangen: string;
  zorgen: string;
  verwachtingen: string;
  invloedkanalen: string;
  aandachtspunten: string;
  bron: 'handmatig' | 'gebiedsanalyse' | 'ai' | 'projectverkenning';
}

const NIVEAU_OPTS: { v: Niveau; l: string }[] = [
  { v: 'laag', l: 'Laag' }, { v: 'midden', l: 'Midden' }, { v: 'hoog', l: 'Hoog' }, { v: 'zeer_hoog', l: 'Zeer hoog' },
];
const HOUDING_OPTS: { v: Houding; l: string; c: string }[] = [
  { v: 'zeer_negatief', l: 'Zeer negatief', c: '#b91c1c' },
  { v: 'negatief', l: 'Negatief', c: '#d97706' },
  { v: 'neutraal', l: 'Neutraal', c: '#6b7280' },
  { v: 'positief', l: 'Positief', c: '#1f7a44' },
  { v: 'zeer_positief', l: 'Zeer positief', c: '#15803d' },
];
const TYPE_OPTS: { v: StakeholderType; l: string }[] = [
  { v: 'groep', l: 'Groep' }, { v: 'organisatie', l: 'Organisatie' }, { v: 'individu', l: 'Individu' },
];

const nScore = (n: Niveau): number => ({ laag: 1, midden: 2, hoog: 3, zeer_hoog: 4 }[n] || 1);
const houdingLabel = (h: Houding) => HOUDING_OPTS.find((o) => o.v === h) || HOUDING_OPTS[2];
const niveauLabel = (n: Niveau) => NIVEAU_OPTS.find((o) => o.v === n)?.l || n;

/** Mendelow: aanpak afgeleid uit invloed × belang. */
export function aanpakVoor(invloed: Niveau, belang: Niveau): { key: string; label: string; color: string; bg: string } {
  const hi = nScore(invloed) >= 3;
  const hb = nScore(belang) >= 3;
  if (hi && hb) return { key: 'nauw', label: 'Nauw betrekken', color: '#B03A6A', bg: '#FCE8EF' };
  if (hi && !hb) return { key: 'tevreden', label: 'Tevreden houden', color: '#d97706', bg: '#fef4e6' };
  if (!hi && hb) return { key: 'informeren', label: 'Informeren', color: '#2563eb', bg: '#eef3fe' };
  return { key: 'monitoren', label: 'Monitoren', color: '#1f9d55', bg: '#eafaf0' };
}

/** Kleur/label voor een invloed-/belang-niveau (stip in de lijst). */
export function niveauDot(n: Niveau): { color: string; label: string } {
  const label = niveauLabel(n);
  if (nScore(n) >= 3) return { color: '#1e3a2f', label };
  if (n === 'midden') return { color: '#9aa1b0', label };
  return { color: '#cbd5e1', label };
}

const typeIcon = (type: StakeholderType, size = 18): React.ReactNode => {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (type === 'organisatie') return (<svg {...common}><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-3" /><path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01" /></svg>);
  if (type === 'individu') return (<svg {...common}><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 12 0v1" /></svg>);
  return (<svg {...common}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
};

let sIdSeq = 0;
function newSId(): string {
  sIdSeq += 1;
  try { if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) return (crypto as any).randomUUID(); } catch (e) { /* fallback */ }
  return `s-${sIdSeq}-${Math.round(Math.random() * 1e6)}`;
}

export function emptyStakeholder(): Stakeholder {
  return { id: newSId(), naam: '', type: 'groep', groep: '', invloed: 'midden', belang: 'midden', houding: 'neutraal', belangen: '', zorgen: '', verwachtingen: '', invloedkanalen: '', aandachtspunten: '', bron: 'handmatig' };
}

/** Tekstsamenvatting voor downstream fasen (persona-AI leest content.stakeholders als tekst). */
export function stakeholdersToText(list: Stakeholder[]): string {
  return list.filter((s) => s.naam.trim()).map((s) => (s.groep ? `${s.naam} (${s.groep})` : s.naam)).join(', ');
}

/** Migratie: gebruik de opgeslagen lijst; anders parse het oude platte tekstveld. */
/**
 * Sla de stakeholderlijst op binnen de actuele sectie 'omgevingsanalyse'. De backend vervangt de
 * sectie-inhoud in z'n geheel, dus de rest van de opgeslagen inhoud gaat ongewijzigd mee.
 * Geeft false als de fase (inmiddels) gevalideerd is.
 */
export async function saveStakeholdersInDossier(projectId: string, list: Stakeholder[]): Promise<boolean> {
  const res = await dossierAPI.getByProject(projectId);
  const section = res?.dossier?.sections?.omgevingsanalyse;
  if (section?.status === 'locked') return false;
  const current = (section && section.content) || {};
  await dossierAPI.saveSection(projectId, 'omgevingsanalyse', { ...current, stakeholders: stakeholdersToText(list), stakeholdersList: list });
  return true;
}

export function migrateStakeholders(list: any, oudeTekst?: string): Stakeholder[] {
  if (Array.isArray(list) && list.length) {
    return list.map((s: any) => ({ ...emptyStakeholder(), ...s, id: s.id || newSId() }));
  }
  const txt = (oudeTekst || '').trim();
  if (!txt) return [];
  return txt.split(/[,;\n]+/).map((n) => n.trim()).filter(Boolean).map((naam) => ({ ...emptyStakeholder(), naam }));
}

/** Voorstellen (AI of seed) toevoegen zonder bestaande te overschrijven; dedup op naam. */
export function mergeProposals(existing: Stakeholder[], proposals: Partial<Stakeholder>[]): Stakeholder[] {
  const have = new Set(existing.map((s) => s.naam.trim().toLowerCase()));
  const add = proposals
    .filter((p) => p.naam && !have.has(String(p.naam).trim().toLowerCase()))
    .map((p) => ({ ...emptyStakeholder(), ...p, id: newSId() } as Stakeholder));
  return [...existing, ...add];
}

// Split op komma's/puntkomma's, maar NIET binnen haakjes (zodat "(a, b)" heel blijft).
function splitTopLevel(s: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of s) {
    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth = Math.max(0, depth - 1);
    if ((ch === ',' || ch === ';') && depth === 0) { out.push(cur); cur = ''; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((x) => x.replace(/^\s*[-*•]\s*/, '').trim()).filter(Boolean);
}

/** Zet de "Belangrijkste stakeholders" uit de projectverkenning (Fase 1) om naar losse stakeholders. */
export function seedFromFase1(text: string): Stakeholder[] {
  const raw = (text || '').replace(/\r/g, '');
  if (!raw.trim()) return [];
  // Meerdere regels? Dan is elke regel één stakeholder (bullets strippen).
  // Eén regel? Dan splitsen op komma's buiten haakjes.
  const lines = raw.split('\n').map((l) => l.replace(/^\s*[-*•]\s*/, '').trim()).filter(Boolean);
  const entries = lines.length > 1 ? lines : splitTopLevel(lines[0] || '');
  // Lopende tekst is geen stakeholder: een naam is kort en bevat geen zinnen (Veerhavens kreeg zo
  // hele alinea's uit de projectverkenning als 'stakeholder'). Die tekst blijft gewoon in Fase 1 staan.
  // Zinsgrens = woord van 4+ letters gevolgd door . ! of ? en meer tekst (afkortingen als "St." tellen niet).
  const isNaam = (naam: string) => naam.length <= 80 && !/[a-zà-ÿ]{4,}[.!?]\s+\S/i.test(naam);
  const items = entries.map((entry) => {
    const m = entry.match(/^(.+?)\s*\((.+)\)\s*$/); // "Naam (beschrijving)"
    const naam = (m ? m[1] : entry).trim();
    if (!isNaam(naam)) return null;
    const beschrijving = m ? m[2].trim() : '';
    return {
      naam, type: 'groep' as StakeholderType, invloed: 'midden' as Niveau, belang: 'hoog' as Niveau,
      houding: 'neutraal' as Houding, aandachtspunten: beschrijving, bron: 'projectverkenning' as const,
    };
  }).filter((x): x is NonNullable<typeof x> => !!x && !!x.naam);
  return mergeProposals([], items);
}

/** Voorstel op basis van de gebiedsanalyse (client-side, geen AI-call). */
export function seedFromArea(area: any): Stakeholder[] {
  const out: Partial<Stakeholder>[] = [
    { naam: 'Bewoners', type: 'groep', groep: 'Direct omwonenden', invloed: 'midden', belang: 'zeer_hoog', houding: 'neutraal', bron: 'gebiedsanalyse' },
    { naam: 'Ondernemers', type: 'groep', groep: 'Lokale ondernemers', invloed: 'midden', belang: 'hoog', houding: 'neutraal', bron: 'gebiedsanalyse' },
    { naam: 'Gemeente', type: 'organisatie', groep: 'Bevoegd gezag', invloed: 'zeer_hoog', belang: 'hoog', houding: 'positief', bron: 'gebiedsanalyse' },
  ];
  const orgs = Array.isArray(area?.organisaties?.items) ? area.organisaties.items.slice(0, 6) : [];
  orgs.forEach((o: any) => out.push({
    naam: o.naam || o.name || o.label || 'Organisatie',
    type: 'organisatie',
    groep: o.categorie || o.category || o.type || 'Maatschappelijke organisatie',
    invloed: 'laag', belang: 'midden', houding: 'neutraal', bron: 'gebiedsanalyse',
  }));
  return mergeProposals([], out);
}

const fieldLabel: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, display: 'block' };
function ctrlStyle(disabled: boolean): React.CSSProperties {
  return { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid #d8dce4', fontSize: 14, fontFamily: 'inherit', color: '#20242e', background: disabled ? '#f6f7f9' : '#fff', boxSizing: 'border-box' };
}
const bronLabel: Record<Stakeholder['bron'], string> = { handmatig: 'Handmatig', gebiedsanalyse: 'Uit gebiedsanalyse', ai: 'AI-voorstel', projectverkenning: 'Uit projectverkenning' };

// Matrix-cellen in 2x2-volgorde (Invloed verticaal: hoog boven; Belang horizontaal: hoog rechts).
export const MATRIX_CELLS = [
  { key: 'tevreden', label: 'Tevreden houden', color: '#d97706', bg: '#fef4e6' }, // hoge invloed, laag belang
  { key: 'nauw', label: 'Nauw betrekken', color: '#B03A6A', bg: '#FCE8EF' },       // hoge invloed, hoog belang
  { key: 'monitoren', label: 'Monitoren', color: '#1f9d55', bg: '#eafaf0' },       // lage invloed, laag belang
  { key: 'informeren', label: 'Informeren', color: '#2563eb', bg: '#eef3fe' },     // lage invloed, hoog belang
];

const cardStyle: React.CSSProperties = { background: '#fff', border: '1px solid #ececf1', borderRadius: 14, padding: 20 };

const INVLOED_DESC: Record<Niveau, string> = {
  laag: 'Beperkte invloed op de besluitvorming.',
  midden: 'Kan het project beïnvloeden via netwerk of media.',
  hoog: 'Kan het project sterk beïnvloeden.',
  zeer_hoog: 'Doorslaggevende invloed op de besluitvorming.',
};
const BELANG_DESC: Record<Niveau, string> = {
  laag: 'Beperkt geraakt door het project.',
  midden: 'Merkt de gevolgen van het project.',
  hoog: 'Direct en sterk geraakt door het project.',
  zeer_hoog: 'Zeer groot belang bij de uitkomst.',
};
const AANPAK_DESC: Record<string, string> = {
  nauw: 'Betrek nauw en vroegtijdig; co-creëer waar mogelijk.',
  tevreden: 'Houd tevreden en goed geïnformeerd; voorkom verrassingen.',
  informeren: 'Houd actief en tijdig geïnformeerd; luister naar zorgen.',
  monitoren: 'Monitor met beperkte inspanning; informeer waar nodig.',
};

const LevelDots: React.FC<{ n: Niveau; color: string }> = ({ n, color }) => {
  const filled = nScore(n);
  return (
    <span style={{ display: 'inline-flex', gap: 5 }}>
      {[1, 2, 3, 4].map((i) => <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: i <= filled ? color : '#e6e8ee' }} />)}
    </span>
  );
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string; desc: string; color: string; bg: string; footer?: React.ReactNode }> = ({ icon, label, value, desc, color, bg, footer }) => (
  <div style={{ ...cardStyle, padding: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
      <span style={{ width: 34, height: 34, borderRadius: 9, background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 12, color: '#8a90a0', fontWeight: 600 }}>{label}</span>
    </div>
    <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>{value}</div>
    <div style={{ fontSize: 12, color: '#8a90a0', lineHeight: 1.45, margin: '4px 0 10px', minHeight: 34 }}>{desc}</div>
    {footer}
  </div>
);

/** Compacte positie-matrix die de plek van deze stakeholder toont. */
const MiniMatrix: React.FC<{ s: Stakeholder }> = ({ s }) => {
  const activeKey = aanpakVoor(s.invloed, s.belang).key;
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 18, width: 14 }}>
        <span style={{ fontSize: 9, color: '#bcc2cc' }}>Hoog</span>
        <span style={{ fontSize: 9.5, color: '#9aa1b0', fontWeight: 600, transform: 'rotate(-90deg)' }}>Invloed</span>
        <span style={{ fontSize: 9, color: '#bcc2cc' }}>Laag</span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {MATRIX_CELLS.map((c) => {
            const active = c.key === activeKey;
            return (
              <div key={c.key} style={{ background: active ? c.bg : '#f7f8fa', border: `1px solid ${active ? c.color : '#eef0f4'}`, borderRadius: 8, padding: '10px 6px', minHeight: 54, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                {active && <span style={{ width: 11, height: 11, borderRadius: '50%', background: c.color }} />}
                <span style={{ fontSize: 10, fontWeight: 600, color: active ? c.color : '#aab0ba', textAlign: 'center', lineHeight: 1.2 }}>{c.label}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 9, color: '#bcc2cc' }}>
          <span>Laag</span><span style={{ fontWeight: 600, color: '#9aa1b0' }}>Belang</span><span>Hoog</span>
        </div>
      </div>
    </div>
  );
};

const sectionLabel: React.CSSProperties = { fontSize: 13.5, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 };
const sectionHint: React.CSSProperties = { fontSize: 12, color: '#8a90a0', marginBottom: 8 };
const STAKEHOLDER_SECTIES: { k: keyof Stakeholder; label: string; hint: string }[] = [
  { k: 'belangen', label: 'Belangen', hint: 'Wat wil deze stakeholder bereiken of behouden?' },
  { k: 'zorgen', label: 'Zorgen & wensen', hint: 'Waar maakt hij/zij zich zorgen over? Welke wensen zijn er?' },
  { k: 'verwachtingen', label: 'Verwachtingen & informatiebehoefte', hint: 'Wat verwacht men van het project en welke info is nodig?' },
  { k: 'invloedkanalen', label: 'Invloed & kanalen', hint: 'Hoe oefent deze stakeholder invloed uit? Via welke kanalen bereik je hem/haar?' },
  { k: 'aandachtspunten', label: 'Overige notities', hint: '' },
];

/** Rijke, bewerkbare detailpagina voor één stakeholder. */
export const StakeholderForm: React.FC<{ s: Stakeholder; locked: boolean; onChange: (k: keyof Stakeholder, v: string) => void }> = ({ s, locked, onChange }) => {
  const aanpak = aanpakVoor(s.invloed, s.belang);
  const houding = houdingLabel(s.houding);
  return (
    <>
      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 18 }}>
        <StatCard icon={typeIcon('groep', 18)} label="Invloed" value={niveauLabel(s.invloed)} desc={INVLOED_DESC[s.invloed]} color="#2563eb" bg="#eef3fe" footer={<LevelDots n={s.invloed} color="#2563eb" />} />
        <StatCard icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4" /></svg>} label="Belang" value={niveauLabel(s.belang)} desc={BELANG_DESC[s.belang]} color="#1e3a2f" bg="#e8eee6" footer={<LevelDots n={s.belang} color="#1e3a2f" />} />
        <StatCard icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18" /><path d="M5 7l7-3 7 3" /><path d="M3 12l2-5 2 5a2.5 2.5 0 0 1-4 0z" /><path d="M17 12l2-5 2 5a2.5 2.5 0 0 1-4 0z" /></svg>} label="Houding" value={houding.l} desc="Houding ten opzichte van het project." color={houding.c} bg="#f6f7f9" footer={<span style={{ width: 10, height: 10, borderRadius: '50%', background: houding.c, display: 'inline-block' }} />} />
        <StatCard icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><polyline points="17 11 19 13 23 9" /></svg>} label="Aanbevolen aanpak" value={aanpak.label} desc={AANPAK_DESC[aanpak.key]} color={aanpak.color} bg={aanpak.bg} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18, alignItems: 'start' }}>
        {/* Hoofdkolom */}
        <div style={{ minWidth: 0 }}>
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <div style={sectionLabel}>Kenmerken</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: 12 }}>
              <div><label style={fieldLabel}>Naam</label><input value={s.naam} disabled={locked} onChange={(e) => onChange('naam', e.target.value)} placeholder="Naam of groep" style={{ ...ctrlStyle(locked), fontWeight: 700 }} /></div>
              <div><label style={fieldLabel}>Type</label><select value={s.type} disabled={locked} onChange={(e) => onChange('type', e.target.value)} style={ctrlStyle(locked)}>{TYPE_OPTS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}</select></div>
            </div>
            <div style={{ marginTop: 12 }}><label style={fieldLabel}>Groep</label><input value={s.groep} disabled={locked} onChange={(e) => onChange('groep', e.target.value)} placeholder="bijv. Oudere omwonenden Dorpsstraat" style={ctrlStyle(locked)} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginTop: 12 }}>
              <div><label style={fieldLabel}>Invloed</label><select value={s.invloed} disabled={locked} onChange={(e) => onChange('invloed', e.target.value)} style={ctrlStyle(locked)}>{NIVEAU_OPTS.map((n) => <option key={n.v} value={n.v}>{n.l}</option>)}</select></div>
              <div><label style={fieldLabel}>Belang</label><select value={s.belang} disabled={locked} onChange={(e) => onChange('belang', e.target.value)} style={ctrlStyle(locked)}>{NIVEAU_OPTS.map((n) => <option key={n.v} value={n.v}>{n.l}</option>)}</select></div>
              <div><label style={fieldLabel}>Houding</label><select value={s.houding} disabled={locked} onChange={(e) => onChange('houding', e.target.value)} style={{ ...ctrlStyle(locked), color: houding.c, fontWeight: 600 }}>{HOUDING_OPTS.map((h) => <option key={h.v} value={h.v} style={{ color: '#20242e' }}>{h.l}</option>)}</select></div>
            </div>
          </div>

          {STAKEHOLDER_SECTIES.map((f) => (
            <div key={f.k as string} style={{ ...cardStyle, marginBottom: 12 }}>
              <div style={sectionLabel}>{f.label}</div>
              {f.hint && <div style={sectionHint}>{f.hint}</div>}
              <RichText value={String(s[f.k] || '')} disabled={locked} onChange={(html) => onChange(f.k, html)} placeholder="Vul in…" minHeight={70} />
            </div>
          ))}
        </div>

        {/* Zijkolom */}
        <div style={{ minWidth: 0, display: 'grid', gap: 16 }}>
          <div style={cardStyle}>
            <div style={sectionLabel}>Positie in de matrix</div>
            <MiniMatrix s={s} />
          </div>
          <div style={cardStyle}>
            <div style={sectionLabel}>Samenvatting</div>
            <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ color: '#8a90a0' }}>Type</span><span style={{ color: '#1a1a2e', fontWeight: 600 }}>{TYPE_OPTS.find((t) => t.v === s.type)?.l}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ color: '#8a90a0' }}>Aanpak</span><span style={{ color: aanpak.color, fontWeight: 700 }}>{aanpak.label}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ color: '#8a90a0' }}>Herkomst</span><span style={{ color: '#1a1a2e', fontWeight: 600 }}>{bronLabel[s.bron]}</span></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/** Visueel overzicht: Stakeholderbalans-matrix + Top stakeholders-lijst. */
export const StakeholdersOverview: React.FC<{
  stakeholders: Stakeholder[];
  locked: boolean;
  area: any;
  hasFase1?: boolean;
  onOpen: (id: string) => void;
  onCreate: () => void;
  onSeed: () => void;
  onSeedFase1?: () => void;
  /** Kop + uitleg tonen; uit op de eigen Stakeholders-pagina (die heeft een paginakop). */
  showHeading?: boolean;
}> = ({ stakeholders, locked, area, hasFase1, onOpen, onCreate, onSeed, onSeedFase1, showHeading = true }) => {
  const byKey: Record<string, Stakeholder[]> = { tevreden: [], nauw: [], monitoren: [], informeren: [] };
  stakeholders.forEach((s) => { byKey[aanpakVoor(s.invloed, s.belang).key].push(s); });
  const sorted = [...stakeholders].sort((a, b) => (nScore(b.invloed) + nScore(b.belang)) - (nScore(a.invloed) + nScore(a.belang)));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: showHeading ? 'space-between' : 'flex-end', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        {showHeading && (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>Stakeholders & stakeholderanalyse</div>
            <div style={{ fontSize: 12.5, color: '#8a90a0' }}>Wie worden geraakt of hebben invloed? Klik een stakeholder aan om te bewerken.</div>
          </div>
        )}
        {!locked && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {hasFase1 && (
              <button onClick={onSeedFase1} style={{ padding: '8px 13px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Uit projectverkenning
              </button>
            )}
            {area && (
              <button onClick={onSeed} style={{ padding: '8px 13px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: '1px solid #b9c8b4', background: '#fff', color: '#1e3a2f', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Voorstel uit gebiedsanalyse
              </button>
            )}
            <button onClick={onCreate} style={{ padding: '8px 13px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', background: '#1a1a2e', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              + Stakeholder
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        {/* Stakeholderbalans */}
        <div style={cardStyle}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 14 }}>Stakeholderbalans</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 22, width: 18 }}>
              <span style={{ fontSize: 10, color: '#9aa1b0' }}>Hoog</span>
              <span style={{ fontSize: 10.5, color: '#8a90a0', fontWeight: 600, transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>Invloed</span>
              <span style={{ fontSize: 10, color: '#9aa1b0' }}>Laag</span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {MATRIX_CELLS.map((cell) => {
                  const items = byKey[cell.key] || [];
                  return (
                    <div key={cell.key} style={{ background: cell.bg, borderRadius: 12, padding: 12, minHeight: 104, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: cell.color, marginBottom: 8 }}>{cell.label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                        {items.slice(0, 5).map((s) => (
                          <span key={s.id} title={s.naam} style={{ width: 28, height: 28, borderRadius: '50%', background: '#fff', color: cell.color, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                            {typeIcon(s.type, 15)}
                          </span>
                        ))}
                        {items.length > 5 && <span style={{ fontSize: 11, fontWeight: 700, color: cell.color }}>+{items.length - 5}</span>}
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1a2e', alignSelf: 'center', marginTop: 4 }}>{items.length}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: 10, color: '#9aa1b0' }}>
                <span>Laag</span>
                <span style={{ fontSize: 10.5, color: '#8a90a0', fontWeight: 600 }}>Belang</span>
                <span>Hoog</span>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 14, borderTop: '1px solid #f0f1f4', paddingTop: 12, fontSize: 12.5, color: '#8a90a0' }}>
            <strong style={{ color: '#1e3a2f' }}>{stakeholders.length}</strong> stakeholder{stakeholders.length === 1 ? '' : 's'} in kaart
          </div>
        </div>

        {/* Top stakeholders */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>Top stakeholders</div>
            <div style={{ width: 84, fontSize: 11.5, color: '#9aa1b0', textAlign: 'left' }}>Invloed</div>
            <div style={{ width: 84, fontSize: 11.5, color: '#9aa1b0', textAlign: 'left' }}>Belang</div>
          </div>
          {sorted.length === 0 && (
            <div style={{ padding: '18px 0', color: '#8a90a0', fontSize: 13 }}>
              Nog geen stakeholders. Voeg ze toe{area ? ', neem een voorstel over uit de gebiedsanalyse,' : ''} of laat de AI een voorstel doen via “Concept-analyse voorstellen” in de omgevingsanalyse.
            </div>
          )}
          <div>
            {sorted.map((s, i) => {
              const aanpak = aanpakVoor(s.invloed, s.belang);
              const inv = niveauDot(s.invloed);
              const bel = niveauDot(s.belang);
              return (
                <button key={s.id} onClick={() => onOpen(s.id)}
                  style={{ display: 'flex', alignItems: 'center', width: '100%', textAlign: 'left', gap: 12, padding: '10px 0', border: 'none', borderTop: i === 0 ? 'none' : '1px solid #f2f3f5', background: 'transparent', cursor: 'pointer' }}>
                  <span style={{ width: 38, height: 38, borderRadius: '50%', background: aanpak.bg, color: aanpak.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {typeIcon(s.type, 19)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.naam || 'Naamloos'}</div>
                    {s.groep && <div style={{ fontSize: 12, color: '#9aa1b0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.groep}</div>}
                  </div>
                  <div style={{ width: 84, display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: inv.color }} />
                    <span style={{ fontSize: 12.5, color: '#374151' }}>{inv.label}</span>
                  </div>
                  <div style={{ width: 84, display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: bel.color }} />
                    <span style={{ fontSize: 12.5, color: '#374151' }}>{bel.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
          {!locked && (
            <button onClick={onCreate} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 12, padding: 0, border: 'none', background: 'transparent', color: '#1e3a2f', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Stakeholder toevoegen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
